import { useMemo } from 'react'

import { matchFilterNumber, matchFilterString, sortArrayBy } from '@domain-group/javascript-utils'

import { useWatchEntityUpdates, useLoadMore } from '@domain-group/react-hooks'

import useReduxAction from '@hooks/useReduxAction'
import useSelector from '@hooks/useSelector'

import type { EntityOptions } from '@redux/modules/types'

const watchEntityKeys = ['providerOrderCodes']

export type ProviderOrderCodesFilter = {
  string?: string,
  provider_id?: number,
  provider_price_list_id?: number,
  service_id?: number,
  service_product_id?: number,
}

interface UseProviderCodesOptions {
  entityKey?: string,
  filters?: ProviderOrderCodesFilter,
  performHttpRequests?: boolean,
  requestOptions?: EntityOptions,
}

function useProviderOrderCodes(options: UseProviderCodesOptions = {}) {
  const { entityKey, filters, requestOptions } = options
  const {
    provider_id: filterProviderId,
    provider_price_list_id: filterProviderPriceListId,
    service_id: filterServiceId,
    service_product_id: filterServiceProductId,
    string: filterString,
  } = filters || {}

  const {
    updatedEntities: { providerOrderCodes: providerOrderCodesUpdatedAt },
  } = useWatchEntityUpdates(watchEntityKeys)

  const { providerOrderCodes, serviceProducts } = useSelector(reduxState => reduxState.entities)

  const filteredProviderOrderCodes = useMemo(() => {
    const filtered = Object.values(providerOrderCodes).filter((providerOrderCode) => {
      const {
        id, provider_code, provider_id, provider_price_list_id, service_product_id,
      } = providerOrderCode

      const matchProviderId = matchFilterNumber(provider_id, filterProviderId)
      const matchProviderPriceListId = matchFilterNumber(provider_price_list_id, filterProviderPriceListId)
      const matchServiceProductId = matchFilterNumber(service_product_id, filterServiceProductId)

      const serviceProduct = serviceProducts[service_product_id] || {}
      const matchServiceId = matchFilterNumber(serviceProduct.service_id, filterServiceId)

      const matchProviderCode = matchFilterString(provider_code, filterString)
      const matchProviderCodeId = id.toString() === filterString
      const matchString = matchProviderCode || matchProviderCodeId

      return matchProviderId
        && matchProviderPriceListId
        && matchServiceId
        && matchServiceProductId
        && matchString
    })

    return sortArrayBy(filtered, 'asc')
  }, [providerOrderCodesUpdatedAt, JSON.stringify(filters)])

  const filteredProviderOrderCodesCount = filteredProviderOrderCodes.length
  const hasFilteredProviderOrderCodes = !!filteredProviderOrderCodes.length

  const loadMorePayload = useLoadMore({
    filters,
    loadedCount: filteredProviderOrderCodesCount,
    performHttpRequests: options.performHttpRequests,
  })

  const {
    callbacks: { loadMore },
    canLoadMore,
    limit,
    performHttpRequests,
  } = loadMorePayload

  const { loading: loadingProviderOrderCodes } = useReduxAction(
    'providerOrderCodes',
    'loadProviderOrderCodes',
    {
      entityKey,
      ...filters,
      ...requestOptions,
      limit,
    },
    [filters, performHttpRequests],
    {
      // @ts-expect-error TS2339
      shouldPerformFn: ({ loading }) => performHttpRequests && !loading,
    },
  )

  return {
    callbacks: {
      loadMore,
    },
    canLoadMore,
    filteredProviderOrderCodes,
    filteredProviderOrderCodesCount,
    hasFilteredProviderOrderCodes,
    loading: loadingProviderOrderCodes,
  }
}

export default useProviderOrderCodes
